/* eslint-disable jsx-a11y/label-has-for */
import { renderToString } from 'react-dom/server'
import i18n from 'i18next'
import { useTranslation } from 'react-i18next'
import styled, { ServerStyleSheet } from 'styled-components'

import { normalizeLocale } from 'i18n'
import { RailsContextInterface } from 'contexts/RailsContext'
import { media } from 'styles/media'
import FullLogo from 'components/icons/BrandLogoPositive'
import EmailSignup from 'components/form/EmailSignup'
import StaticWrapper from 'pages/static/StaticWrapper'
import Horizon from './Horizon'
import FooterMenu from './FooterMenu'
import SocialMenu from './SocialMenu'
import { FooterLinksTree } from './FooterMenu/ExploreLinks'

const Wrapper = styled.footer`
  width: 100%;
  overflow: hidden;
`

const InnerWrapper = styled.div`
  padding: 24px 0 40px;
  color: ${({ theme }) => theme?.['-ds-neutral-100']?.value};
  background-color: ${({ theme }) => theme.brand.jungle};
`

const Grid = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 20px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 40px;

  ${media.lg`
    grid-template-columns: 320px 1fr;
    gap: 64px;
    padding: 0 64px;
  `}
`

const BrandRail = styled.div`
  display: flex;
  flex-direction: column;
  gap: 60px;
`

const BrandRailFooter = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const Pillars = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 28px 16px;
`

const PillarTitle = styled.div`
  font-size: 11px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(233, 239, 233, 0.9);
  margin-bottom: 12px;
  font-weight: 600;
`

const PillarList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;

  a {
    font-size: 13.5px;
    color: ${({ theme }) => theme?.['-ds-neutral-100']?.value};
    text-decoration: none;

    &:hover {
      color: ${({ theme }) => theme?.['-ds-neutral-300']?.value};
    }
  }
`

const Logo = styled.div`
  color: ${({ theme }) => theme.colors.white};

  svg {
    display: inline-block;
    width: 156px;
  }
`

const Acknowledgement = styled.div`
  font-size: 12.5px;
  line-height: 1.6;
  color: rgba(233, 239, 233, 0.9);
  max-width: 320px;
`

const Legal = styled.div`
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(233, 239, 233, 0.9);

  a {
    color: inherit;
    text-decoration: none;

    &:hover {
      color: ${({ theme }) => theme?.['-ds-neutral-200']?.value};
    }
  }
`

interface FooterProps {
  currentYear: number
  acknowledgement: string
  footerLinks: FooterLinksTree
  marketCode?: string
}

const PILLARS = [
  {
    title: 'Company',
    links: [
      { name: 'Our Story', href: '/about' },
      { name: 'How Riparide Works', href: '/how-it-works' },
      { name: 'Riparide Stories', href: '/stories-travellers' },
      {
        name: 'Careers',
        href: '/jobs',
        rel: 'noopener noreferrer',
        target: '_blank',
      },
    ],
  },
  {
    title: 'Community',
    links: [
      { name: 'Become a Storyteller', href: '/stories-storytellers' },
      { name: 'Become a Host', href: '/list-with-us' },
    ],
  },
  {
    title: 'Gifting',
    links: [
      { name: 'Gift Vouchers', href: '/vouchers' },
      {
        name: 'Employee Vouchers',
        href: 'https://pages.riparide.com/employee-vouchers',
        target: '_blank',
        rel: 'noopener noreferrer',
      },
    ],
  },
  {
    title: 'Support',
    links: [
      {
        name: 'Help Centre',
        href: 'https://help.riparide.com/',
        rel: 'noopener noreferrer',
        target: '_blank',
      },
      { name: 'Contact Us', href: '/contact-us' },
    ],
  },
]

const Footer = ({
  currentYear,
  acknowledgement,
  footerLinks,
  marketCode,
}: FooterProps) => {
  const [t] = useTranslation()

  return (
    <Wrapper>
      <Horizon />

      <InnerWrapper>
        <Grid>
          <BrandRail>
            <EmailSignup
              labelInner="Get the best escapes straight to your inbox"
              buttonLabel="Sign up"
              marketCode={marketCode}
            />

            <Pillars>
              {PILLARS.map((pillar) => (
                <div key={pillar.title}>
                  <PillarTitle>{pillar.title}</PillarTitle>
                  <PillarList>
                    {pillar.links.map((link) => (
                      <li key={link.name}>
                        <a href={link.href} target={link.target} rel={link.rel}>
                          {link.name === 'Help Centre'
                            ? t('helpCenter.name')
                            : link.name}
                        </a>
                      </li>
                    ))}
                  </PillarList>
                </div>
              ))}
            </Pillars>

            <BrandRailFooter>
              <Logo>
                <FullLogo />
              </Logo>

              <Acknowledgement
                dangerouslySetInnerHTML={{ __html: acknowledgement }}
              />

              <SocialMenu />

              <Legal>
                <div>#livefully with Riparide</div>
                <div>
                  &copy; {currentYear} Riparide Pty Ltd &nbsp;·&nbsp;
                  <a href="/terms">Terms</a> &nbsp;·&nbsp;
                  <a href="/privacy">Privacy</a>
                </div>
              </Legal>
            </BrandRailFooter>
          </BrandRail>

          <FooterMenu footerLinks={footerLinks} />
        </Grid>
      </InnerWrapper>
    </Wrapper>
  )
}

export default (props: FooterProps, railsContext: RailsContextInterface) => {
  // Sync i18next to the per-request locale BEFORE rendering so the SSR
  // HTML and the first hydration render agree on which translations to
  // use. See client/src/App.tsx for the full rationale.
  i18n.changeLanguage(normalizeLocale(railsContext.i18nLocale))

  const rendered = (
    <StaticWrapper {...railsContext}>
      <Footer {...props} />
    </StaticWrapper>
  )

  // SSR: collect styled-components styles outside the React tree so they
  // can be emitted into <head> via react_component_hash. See App.tsx for
  // the full rationale (React 18 hydration #418/#425/#423 cascade).
  if (railsContext.serverSide) {
    const sheet = new ServerStyleSheet()
    try {
      const componentHtml = renderToString(sheet.collectStyles(rendered))
      const componentStyles = sheet.getStyleTags()
      return { renderedHtml: { componentHtml, componentStyles } }
    } finally {
      sheet.seal()
    }
  }

  return () => rendered
}
